import type { DocNode } from '@atlaskit/adf-schema/doc';
import type { JSONNode } from '@atlaskit/editor-json-transformer/types';

import {
	REMIX_EXPANDS_EXPERIMENT,
	isRemixRestrictedNodeTypeWithExpands,
} from '@atlassian/malleable-ui-components/remix/remix-config';

import { isExperimentEnabled } from '@confluence/feature-experiments';

import { filterRemixFragmentNodes } from './filterRemixFragmentNodes.ts';

/** Only invoked for expand node types, so expand-free documents never read the experiment. */
const areRemixExpandsEnabled = (): boolean => isExperimentEnabled(REMIX_EXPANDS_EXPERIMENT);

/**
 * Block types that become direct siblings in page remix `fragmentAdf` after flattening.
 * Containers (layouts, lists, tables, panels, …) are unwrapped so the result matches a
 * linear `doc.content`-style array of block nodes in document order.
 */
const FRAGMENT_ROOT_BLOCK_TYPES = new Set<string>(['paragraph', 'heading', 'codeBlock', 'rule']);

function flattenRemixFragmentNodes(nodes: JSONNode[] | undefined | null): JSONNode[] {
	if (!nodes?.length) {
		return [];
	}
	const out: JSONNode[] = [];
	const stack: JSONNode[] = [...nodes].reverse();

	while (stack.length) {
		const node = stack.pop();
		if (!node || typeof node !== 'object' || !('type' in node)) {
			continue;
		}
		const nodeType = String((node as { type: string }).type);
		// Expands are skipped entirely unless REMIX_EXPANDS_EXPERIMENT is on, in which case they
		// fall through to the container branch below and their inner blocks are flattened in like
		// any other container. Page remix has no wrapper to preserve, so the expand title is
		// dropped the same way layout and table wrappers are.
		if (isRemixRestrictedNodeTypeWithExpands(nodeType, areRemixExpandsEnabled)) {
			continue;
		}
		if (FRAGMENT_ROOT_BLOCK_TYPES.has(nodeType)) {
			const filtered = filterRemixFragmentNodes([node]);
			if (filtered?.length) {
				out.push(...filtered);
			}
			continue;
		}
		const content = (node as { content?: JSONNode[] }).content;
		if (content?.length) {
			stack.push(...[...content].reverse());
		}
	}
	return out;
}

/**
 * Walks the document tree and returns a single array of block nodes at the same structural
 * “depth” as a typical `doc.content` list: nested layouts, lists, table cells, etc. are
 * flattened so paragraphs (and other root blocks) appear as top-level siblings.
 */
export function flattenDocContentForPageRemixFragment(adfDocument: DocNode): JSONNode[] {
	return flattenRemixFragmentNodes(adfDocument.content ?? []);
}
